import {FC} from 'react'

import {getCountryLandingPageData} from '@/content/countryCards'
import {getCountryGuideContent} from '@/content/countryGuide'
import {useTranslation} from '@/i18n'
import {CountryGuideContent} from '@/utils/contentful'
import {Jurisdiction} from '@/utils/countries/constants'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {routeTo} from '@/utils/router'
import {tmRenewalJurisdictions} from '@/utils/trademark-renewal/constants'

import {ContentCardImageSize} from '../common/content/ContentCard'
import {GuideContentLayout} from '../guide/GuideContentLayout'

type CountryGuideContentLayoutProps = {
  countrySpecificGuide: CountryGuideContent
}

export const CountryGuideContentLayout: FC<CountryGuideContentLayoutProps> = ({
  countrySpecificGuide,
}) => {
  const {t} = useTranslation()
  const {learnMoreText} = getCountryGuideContent()
  const {
    countryCode,
    guide,
    registrationCardTitle,
    registrationCardDescription,
    renewalCardTitle,
    renewalCardDescription,
  } = countrySpecificGuide
  const {route} = useNavigation()
  const hasLandingPage = Boolean(getCountryLandingPageData(countryCode as Jurisdiction))
  const isOfferedJurisdiction = Object.values(Jurisdiction).includes(countryCode as Jurisdiction)

  const linkHref = isOfferedJurisdiction
    ? hasLandingPage
      ? routeTo.landingPage(countryCode as Jurisdiction)
      : routeTo.verification.form({country: countryCode.toLowerCase()})
    : routeTo.internationalTm()

  const trackedEventName = isOfferedJurisdiction
    ? hasLandingPage
      ? 'Country specific page'
      : 'Trademark verification'
    : 'International trademark'

  const cards = [
    {
      title: registrationCardTitle,
      description: registrationCardDescription,
      imageSrc: '/images/icons/trademark-registration.svg',
      imageAlt: 'Trademark registration',
      imageSize: ContentCardImageSize.large,
      buttonText: t(learnMoreText),
      buttonLink: linkHref,
      trackedEvent: {name: trackedEventName, parameters: {pageName: route, countryCode}},
    },
    ...(tmRenewalJurisdictions.includes(countryCode as Jurisdiction) &&
    !!renewalCardTitle &&
    !!renewalCardDescription
      ? [
          {
            title: renewalCardTitle,
            description: renewalCardDescription,
            imageSrc: '/images/icons/renewal.svg',
            imageAlt: 'Trademark registration',
            imageSize: ContentCardImageSize.large,
            buttonText: t(learnMoreText),
            buttonLink: routeTo.trademarkRenewal.home(countryCode as Jurisdiction),
            trackedEvent: {
              name: 'Trademark renewal',
              parameters: {pageName: route, countryCode},
            },
          },
        ]
      : []),
  ]

  return <GuideContentLayout guide={guide} sidebarCards={cards} />
}
